import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { errMsg } from "@/utils";
import { analytics } from "@/services/analytics";
import {
  AuthShell,
  AuthHeading,
  AuthField,
  AuthSubmit,
  Divider,
  GoogleButton,
} from "@/layouts/AuthLayout";
import { useRecaptcha } from "@/hooks/useRecaptcha";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { execute: executeRecaptcha } = useRecaptcha();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const recaptchaToken = await executeRecaptcha("login");
      await login(email, password, recaptchaToken);
      analytics.login("email");
      toast.success("Welcome back.");
      navigate("/");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast.error("Google sign-in is not configured.");
      return;
    }
    const redirectUri = window.location.origin + "/auth/google/callback";
    const scope = "openid email profile";
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope,
      access_type: "offline",
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  return (
    <AuthShell>
      <AuthHeading
        eyebrow="Welcome back"
        title="Log in to your"
        accent="Zenstrust account"
        subtitle="Pick up where you left off — portfolio, advisors and filings in one place."
      />

      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <AuthField label="Email address">
          <input
            type="email"
            required
            autoComplete="email"
            data-testid="login-email"
            className="input-editorial font-body"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </AuthField>

        <AuthField
          label="Password"
          hint={
            <Link
              to="/forgot-password"
              className="font-heading text-[12px] font-semibold text-[hsl(var(--zt-gold))] hover:underline underline-offset-4"
            >
              Forgot password?
            </Link>
          }
        >
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              required
              autoComplete="current-password"
              data-testid="login-password"
              className="input-editorial has-affix font-body"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your account password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-lg text-[hsl(var(--zt-muted))] hover:text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige))] transition-colors duration-200"
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </AuthField>

        <AuthSubmit busy={busy} busyLabel="Signing in…" data-testid="login-submit">
          Log in <ArrowRight size={16} />
        </AuthSubmit>
        {busy && <Loader2 className="sr-only animate-spin" size={16} />}
      </form>

      <Divider />

      <GoogleButton onClick={googleLogin} data-testid="login-google" />

      <p className="font-body text-[13.5px] text-[hsl(var(--zt-charcoal))]/75 text-center pt-1">
        New to Zenstrust?{" "}
        <Link
          to="/signup"
          data-testid="login-to-signup"
          className="font-heading font-semibold text-[hsl(var(--zt-gold))] underline-offset-4 hover:underline"
        >
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}
